import { useState, useEffect } from 'react';
import { ClientAccount } from '../../../contexts/AuthContext';
import { DataItem } from './UploadSection';
import { addNotification } from '../../../services/notifications';

type ProjectStatus = 'Active' | 'In Process' | 'Completed' | 'On Hold';
type TaskStatus = 'Pending' | 'In Progress' | 'Done' | 'Blocked';

interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  dueDate: string;
  createdAt: string;
}

interface Project {
  projectNumber: string;
  projectName: string;
  status: ProjectStatus;
  items: DataItem[];
  tasks: Task[];
}

interface ClientDetailProps {
  client: ClientAccount;
  onBack: () => void;
}

export default function ClientDetail({ client, onBack }: ClientDetailProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState<string | null>(null);
  const [statusDropdown, setStatusDropdown] = useState<string | null>(null);

  // Task management states
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskStatus, setTaskStatus] = useState<TaskStatus>('Pending');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskFormError, setTaskFormError] = useState('');
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState<string | null>(null);
  const [taskStatusDropdown, setTaskStatusDropdown] = useState<string | null>(null);

  // New project form
  const [newProjectNumber, setNewProjectNumber] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectStatus, setNewProjectStatus] = useState<ProjectStatus>('Active');
  const [projectFormError, setProjectFormError] = useState('');

  // Upload form
  const [uploadType, setUploadType] = useState<'file' | 'text'>('file');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textContent, setTextContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  useEffect(() => {
    loadProjects();
  }, [client.id]);

  const loadProjects = () => {
    const stored = localStorage.getItem('admin_data_items');
    const allItems: DataItem[] = stored ? JSON.parse(stored) : [];
    const clientItems = allItems.filter((i) => i.clientId === client.id);

    // Load tasks from localStorage
    const tasksStored = localStorage.getItem('admin_project_tasks');
    const allTasks: Record<string, Task[]> = tasksStored ? JSON.parse(tasksStored) : {};

    const projectMap: Record<string, Project> = {};
    clientItems.forEach((item) => {
      const key = item.projectNumber || 'unassigned';
      if (!projectMap[key]) {
        const taskKey = `${client.id}_${item.projectNumber || 'unassigned'}`;
        projectMap[key] = {
          projectNumber: item.projectNumber || 'Unassigned',
          projectName: item.projectName || 'No Project',
          status: (item as any).projectStatus || 'Active',
          items: [],
          tasks: allTasks[taskKey] || [],
        };
      }
      projectMap[key].items.push(item);
    });

    const sorted = Object.values(projectMap).sort((a, b) =>
      a.projectNumber.localeCompare(b.projectNumber)
    );
    setProjects(sorted);

    // Refresh selected project if open
    if (selectedProject) {
      const refreshed = sorted.find((p) => p.projectNumber === selectedProject.projectNumber);
      setSelectedProject(refreshed || null);
    }
  };

  const handleCreateProject = () => {
    setProjectFormError('');
    if (!newProjectNumber.trim()) { setProjectFormError('Project number is required'); return; }
    if (!newProjectName.trim()) { setProjectFormError('Project name is required'); return; }

    const exists = projects.find((p) => p.projectNumber === newProjectNumber.trim());
    if (exists) { setProjectFormError('A project with this number already exists for this client'); return; }

    // Create a placeholder item to "register" the project
    const stored = localStorage.getItem('admin_data_items');
    const allItems: DataItem[] = stored ? JSON.parse(stored) : [];

    const placeholder: DataItem = {
      id: `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: '__project_placeholder__',
      description: '',
      type: 'text',
      textContent: '',
      uploadedAt: new Date().toISOString(),
      clientId: client.id,
      projectNumber: newProjectNumber.trim(),
      projectName: newProjectName.trim(),
      isPlaceholder: true,
      projectStatus: newProjectStatus,
    } as any;

    allItems.push(placeholder);
    localStorage.setItem('admin_data_items', JSON.stringify(allItems));

    setNewProjectNumber('');
    setNewProjectName('');
    setNewProjectStatus('Active');
    setShowNewProjectForm(false);
    loadProjects();
  };

  const handleUpdateProjectStatus = (projectNumber: string, newStatus: ProjectStatus) => {
    const stored = localStorage.getItem('admin_data_items');
    const allItems: DataItem[] = stored ? JSON.parse(stored) : [];
    
    // Get the old status before updating
    const projectItem = allItems.find(
      (item) => item.clientId === client.id && item.projectNumber === projectNumber
    );
    const oldStatus = (projectItem as any)?.projectStatus || 'Active';
    
    // Update all items in this project with the new status
    const updated = allItems.map((item) => {
      if (item.clientId === client.id && item.projectNumber === projectNumber) {
        return { ...item, projectStatus: newStatus } as any;
      }
      return item;
    });
    
    localStorage.setItem('admin_data_items', JSON.stringify(updated));
    setStatusDropdown(null);
    loadProjects();

    // Fire notification
    const project = projects.find((p) => p.projectNumber === projectNumber);
    if (project && oldStatus !== newStatus) {
      addNotification({
        clientId: client.id,
        clientEmail: client.email,
        type: 'project_status_changed',
        message: `Project status changed from ${oldStatus} to ${newStatus}`,
        projectNumber: project.projectNumber,
        projectName: project.projectName,
      }, client.name);
    }
  };

  const getStatusBadge = (status: ProjectStatus, isClickable = false, projectNumber?: string) => {
    const colors = {
      'Active': 'bg-green-100 text-green-700 border-green-200',
      'In Process': 'bg-blue-100 text-blue-700 border-blue-200',
      'Completed': 'bg-gray-100 text-gray-700 border-gray-200',
      'On Hold': 'bg-amber-100 text-amber-700 border-amber-200',
    };

    const icons = {
      'Active': 'ri-play-circle-fill',
      'In Process': 'ri-time-line',
      'Completed': 'ri-checkbox-circle-fill',
      'On Hold': 'ri-pause-circle-fill',
    };

    if (isClickable && projectNumber) {
      return (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setStatusDropdown(statusDropdown === projectNumber ? null : projectNumber);
            }}
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${colors[status]} hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap`}
          >
            <i className={icons[status]}></i>
            {status}
            <i className="ri-arrow-down-s-line text-xs"></i>
          </button>
          
          {statusDropdown === projectNumber && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[140px]">
              {(['Active', 'In Process', 'Completed', 'On Hold'] as ProjectStatus[]).map((s) => (
                <button
                  key={s}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateProjectStatus(projectNumber, s);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 ${
                    s === status ? 'bg-gray-50' : ''
                  }`}
                >
                  <i className={icons[s]}></i>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${colors[status]} whitespace-nowrap`}>
        <i className={icons[status]}></i>
        {status}
      </span>
    );
  };

  const handleDeleteProject = (projectNumber: string) => {
    const stored = localStorage.getItem('admin_data_items');
    const allItems: DataItem[] = stored ? JSON.parse(stored) : [];
    const updated = allItems.filter(
      (i) => !(i.clientId === client.id && i.projectNumber === projectNumber)
    );
    localStorage.setItem('admin_data_items', JSON.stringify(updated));
    setDeleteProjectConfirm(null);
    if (selectedProject?.projectNumber === projectNumber) setSelectedProject(null);
    loadProjects();
  };

  const handleAddTask = () => {
    setTaskFormError('');
    if (!taskTitle.trim()) { setTaskFormError('Task title is required'); return; }
    if (!selectedProject) return;

    const newTask: Task = {
      id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: taskTitle.trim(),
      description: taskDescription.trim(),
      status: taskStatus,
      dueDate: taskDueDate,
      createdAt: new Date().toISOString(),
    };

    // Load existing tasks
    const tasksStored = localStorage.getItem('admin_project_tasks');
    const allTasks: Record<string, Task[]> = tasksStored ? JSON.parse(tasksStored) : {};
    
    const taskKey = `${client.id}_${selectedProject.projectNumber}`;
    if (!allTasks[taskKey]) allTasks[taskKey] = [];
    allTasks[taskKey].push(newTask);
    
    localStorage.setItem('admin_project_tasks', JSON.stringify(allTasks));
    
    resetTaskForm();
    loadProjects();
  };

  const resetTaskForm = () => {
    setTaskTitle('');
    setTaskDescription('');
    setTaskStatus('Pending');
    setTaskDueDate('');
    setTaskFormError('');
    setShowAddTaskForm(false);
  };

  const handleUpdateTaskStatus = (taskId: string, newStatus: TaskStatus) => {
    if (!selectedProject) return;

    const tasksStored = localStorage.getItem('admin_project_tasks');
    const allTasks: Record<string, Task[]> = tasksStored ? JSON.parse(tasksStored) : {};
    
    const taskKey = `${client.id}_${selectedProject.projectNumber}`;
    if (allTasks[taskKey]) {
      // Get the old status before updating
      const task = allTasks[taskKey].find((t) => t.id === taskId);
      const oldStatus = task?.status;

      allTasks[taskKey] = allTasks[taskKey].map((task) =>
        task.id === taskId ? { ...task, status: newStatus } : task
      );
      localStorage.setItem('admin_project_tasks', JSON.stringify(allTasks));
      setTaskStatusDropdown(null);
      loadProjects();

      // Fire notification
      if (task && oldStatus !== newStatus) {
        addNotification({
          clientId: client.id,
          clientEmail: client.email,
          type: 'task_status_changed',
          message: `Task "${task.title}" status changed from ${oldStatus} to ${newStatus}`,
          projectNumber: selectedProject.projectNumber,
          projectName: selectedProject.projectName,
        }, client.name);
      }
    }
  };

  const handleDeleteTask = (taskId: string) => {
    if (!selectedProject) return;

    const tasksStored = localStorage.getItem('admin_project_tasks');
    const allTasks: Record<string, Task[]> = tasksStored ? JSON.parse(tasksStored) : {};
    
    const taskKey = `${client.id}_${selectedProject.projectNumber}`;
    if (allTasks[taskKey]) {
      allTasks[taskKey] = allTasks[taskKey].filter((task) => task.id !== taskId);
      localStorage.setItem('admin_project_tasks', JSON.stringify(allTasks));
      setDeleteTaskConfirm(null);
      loadProjects();
    }
  };

  const getTaskStatusBadge = (status: TaskStatus, isClickable = false, taskId?: string) => {
    const colors = {
      'Pending': 'bg-gray-100 text-gray-700 border-gray-200',
      'In Progress': 'bg-blue-100 text-blue-700 border-blue-200',
      'Done': 'bg-green-100 text-green-700 border-green-200',
      'Blocked': 'bg-red-100 text-red-700 border-red-200',
    };

    const icons = {
      'Pending': 'ri-time-line',
      'In Progress': 'ri-loader-4-line',
      'Done': 'ri-checkbox-circle-fill',
      'Blocked': 'ri-close-circle-fill',
    };

    if (isClickable && taskId) {
      return (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setTaskStatusDropdown(taskStatusDropdown === taskId ? null : taskId);
            }}
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${colors[status]} hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap`}
          >
            <i className={icons[status]}></i>
            {status}
            <i className="ri-arrow-down-s-line text-xs"></i>
          </button>
          
          {taskStatusDropdown === taskId && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[140px]">
              {(['Pending', 'In Progress', 'Done', 'Blocked'] as TaskStatus[]).map((s) => (
                <button
                  key={s}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateTaskStatus(taskId, s);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 ${
                    s === status ? 'bg-gray-50' : ''
                  }`}
                >
                  <i className={icons[s]}></i>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${colors[status]} whitespace-nowrap`}>
        <i className={icons[status]}></i>
        {status}
      </span>
    );
  };

  const handleUpload = () => {
    setUploadError('');
    setUploadSuccess('');

    if (!uploadTitle.trim()) { setUploadError('Title is required'); return; }
    if (uploadType === 'file' && !selectedFile) { setUploadError('Please select a file'); return; }
    if (uploadType === 'text' && !textContent.trim()) { setUploadError('Text content is required'); return; }
    if (!selectedProject) return;

    setUploading(true);

    const newItem: DataItem = {
      id: `data_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: uploadTitle.trim(),
      description: uploadDescription.trim(),
      type: uploadType,
      uploadedAt: new Date().toISOString(),
      clientId: client.id,
      projectNumber: selectedProject.projectNumber,
      projectName: selectedProject.projectName,
    };

    if (uploadType === 'file' && selectedFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        newItem.fileUrl = e.target?.result as string;
        newItem.fileName = selectedFile.name;
        newItem.fileType = selectedFile.type;
        saveItem(newItem);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      newItem.textContent = textContent.trim();
      saveItem(newItem);
    }
  };

  const saveItem = (item: DataItem) => {
    try {
      const stored = localStorage.getItem('admin_data_items');
      const allItems: DataItem[] = stored ? JSON.parse(stored) : [];
      allItems.push(item);
      localStorage.setItem('admin_data_items', JSON.stringify(allItems));
      setUploadSuccess('Document uploaded successfully!');
      resetUploadForm();
      loadProjects();

      // Fire notification
      if (selectedProject) {
        addNotification({
          clientId: client.id,
          clientEmail: client.email,
          type: 'file_uploaded',
          message: `New document uploaded: "${item.title}"`,
          projectNumber: selectedProject.projectNumber,
          projectName: selectedProject.projectName,
        }, client.name);
      }
    } catch {
      setUploadError('Failed to save. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadTitle('');
    setUploadDescription('');
    setSelectedFile(null);
    setTextContent('');
    setUploadType('file');
    setUploadError('');
    const fileInput = document.getElementById('file-upload-detail') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleDeleteItem = (id: string) => {
    const stored = localStorage.getItem('admin_data_items');
    const allItems: DataItem[] = stored ? JSON.parse(stored) : [];
    const updated = allItems.filter((i) => i.id !== id);
    localStorage.setItem('admin_data_items', JSON.stringify(updated));
    setDeleteConfirm(null);
    loadProjects();
  };

  const getFileIcon = (fileType?: string): string => {
    if (!fileType) return 'ri-file-line';
    if (fileType.includes('pdf')) return 'ri-file-pdf-line';
    if (fileType.includes('word') || fileType.includes('document')) return 'ri-file-word-line';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'ri-file-excel-line';
    if (fileType.includes('image')) return 'ri-image-line';
    return 'ri-file-line';
  };

  const visibleItems = selectedProject
    ? selectedProject.items.filter((i) => !i.isPlaceholder)
    : [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Client Accounts
        </button>
        <i className="ri-arrow-right-s-line text-gray-400"></i>
        <span className="font-semibold text-gray-900">{client.name}</span>
        {selectedProject && (
          <>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="font-semibold text-gray-900">{selectedProject.projectNumber}</span>
          </>
        )}
      </div>

      {/* Client Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center gap-4">
        <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl font-bold">{client.name.charAt(0).toUpperCase()}</span>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{client.name}</h2>
          <p className="text-sm text-gray-500">@{client.username} &bull; {projects.length} {projects.length === 1 ? 'project' : 'projects'}</p>
        </div>
        <div className="ml-auto">
          {client.isActive ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              <i className="ri-checkbox-circle-fill"></i> Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
              <i className="ri-close-circle-fill"></i> Inactive
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Projects Panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Projects</h3>
            <button
              onClick={() => { setShowNewProjectForm(true); setSelectedProject(null); setShowUploadForm(false); }}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i>
              New Project
            </button>
          </div>

          {/* New Project Form */}
          {showNewProjectForm && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Create New Project</h4>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={newProjectNumber}
                  onChange={(e) => setNewProjectNumber(e.target.value)}
                  placeholder="e.g., PRJ-2024-001"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="e.g., Q1 Procurement Initiative"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Status <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {(['Active', 'In Process', 'Completed', 'On Hold'] as ProjectStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => setNewProjectStatus(status)}
                      className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                        newProjectStatus === status
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              {projectFormError && (
                <p className="text-xs text-red-600">{projectFormError}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleCreateProject}
                  className="flex-1 px-3 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Create
                </button>
                <button
                  onClick={() => { setShowNewProjectForm(false); setProjectFormError(''); setNewProjectNumber(''); setNewProjectName(''); setNewProjectStatus('Active'); }}
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Project List */}
          {projects.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
              <i className="ri-folder-line text-3xl text-gray-300 mb-2 block"></i>
              <p className="text-sm text-gray-500">No projects yet</p>
              <p className="text-xs text-gray-400 mt-1">Create a project to start uploading documents</p>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((project) => {
                const docCount = project.items.filter((i) => !i.isPlaceholder).length;
                const isSelected = selectedProject?.projectNumber === project.projectNumber;
                return (
                  <div
                    key={project.projectNumber}
                    className={`group relative rounded-lg border transition-all cursor-pointer ${
                      isSelected
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white hover:border-gray-400'
                    }`}
                  >
                    <div
                      className="p-4"
                      onClick={() => { setSelectedProject(project); setShowUploadForm(false); setUploadSuccess(''); setStatusDropdown(null); }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                            {project.projectNumber}
                          </p>
                          <p className={`text-xs truncate mt-0.5 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                            {project.projectName}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <div onClick={(e) => e.stopPropagation()}>
                              {getStatusBadge(project.status, !isSelected, project.projectNumber)}
                            </div>
                          </div>
                          <p className={`text-xs mt-1.5 ${isSelected ? 'text-gray-400' : 'text-gray-400'}`}>
                            {docCount} {docCount === 1 ? 'document' : 'documents'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {deleteProjectConfirm === project.projectNumber ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleDeleteProject(project.projectNumber)}
                                className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 whitespace-nowrap cursor-pointer"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => setDeleteProjectConfirm(null)}
                                className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 whitespace-nowrap cursor-pointer"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteProjectConfirm(project.projectNumber); }}
                              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer opacity-0 group-hover:opacity-100 ${
                                isSelected ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              }`}
                              title="Delete project"
                            >
                              <i className="ri-delete-bin-line text-xs"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Tasks & Documents Panel */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedProject ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-16 text-center">
              <i className="ri-folder-open-line text-5xl text-gray-200 mb-3 block"></i>
              <p className="text-base font-medium text-gray-500">Select a project to view its tasks and documents</p>
              <p className="text-sm text-gray-400 mt-1">Or create a new project on the left</p>
            </div>
          ) : (
            <>
              {/* Project Header */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-gray-900">{selectedProject.projectNumber} — {selectedProject.projectName}</h3>
                    {getStatusBadge(selectedProject.status)}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedProject.tasks.length} {selectedProject.tasks.length === 1 ? 'task' : 'tasks'} &bull; {visibleItems.length} {visibleItems.length === 1 ? 'document' : 'documents'}
                  </p>
                </div>
                <button
                  onClick={() => { setShowUploadForm(!showUploadForm); setUploadSuccess(''); setUploadError(''); }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={showUploadForm ? 'ri-close-line' : 'ri-upload-2-line'}></i>
                  {showUploadForm ? 'Cancel' : 'Upload Document'}
                </button>
              </div>

              {/* Tasks Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-task-line text-base"></i>
                    Tasks
                  </h4>
                  <button
                    onClick={() => { setShowAddTaskForm(!showAddTaskForm); setTaskFormError(''); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className={showAddTaskForm ? 'ri-close-line' : 'ri-add-line'}></i>
                    {showAddTaskForm ? 'Cancel' : 'Add Task'}
                  </button>
                </div>

                {/* Add Task Form */}
                {showAddTaskForm && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Task Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                        placeholder="e.g., Review contract documents"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                      <textarea
                        value={taskDescription}
                        onChange={(e) => setTaskDescription(e.target.value)}
                        placeholder="Add details about this task..."
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">Status</label>
                        <div className="grid grid-cols-2 gap-2">
                          {(['Pending', 'In Progress', 'Done', 'Blocked'] as TaskStatus[]).map((status) => (
                            <button
                              key={status}
                              onClick={() => setTaskStatus(status)}
                              className={`px-2 py-1.5 text-xs font-medium rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                                taskStatus === status
                                  ? 'border-gray-900 bg-gray-900 text-white'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              {status}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Due Date (optional)</label>
                        <input
                          type="date"
                          value={taskDueDate}
                          onChange={(e) => setTaskDueDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none cursor-pointer"
                        />
                      </div>
                    </div>
                    {taskFormError && (
                      <p className="text-xs text-red-600"><i className="ri-error-warning-line mr-1"></i>{taskFormError}</p>
                    )}
                    <button
                      onClick={handleAddTask}
                      className="w-full px-3 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-add-line mr-1"></i>Create Task
                    </button>
                  </div>
                )}

                {/* Tasks List */}
                {selectedProject.tasks.length === 0 ? (
                  <div className="text-center py-6">
                    <i className="ri-task-line text-3xl text-gray-200 mb-2 block"></i>
                    <p className="text-xs text-gray-500">No tasks yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Click "Add Task" to create the first one</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedProject.tasks
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((task) => (
                        <div key={task.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-all">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-sm font-semibold text-gray-900">{task.title}</p>
                                <div onClick={(e) => e.stopPropagation()}>
                                  {getTaskStatusBadge(task.status, true, task.id)}
                                </div>
                              </div>
                              {task.description && (
                                <p className="text-xs text-gray-600 mb-2">{task.description}</p>
                              )}
                              <div className="flex items-center gap-3 text-xs text-gray-400">
                                {task.dueDate && (
                                  <span className="flex items-center gap-1">
                                    <i className="ri-calendar-line"></i>
                                    Due: {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <i className="ri-time-line"></i>
                                  Created: {new Date(task.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {deleteTaskConfirm === task.id ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleDeleteTask(task.id)}
                                    className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 whitespace-nowrap cursor-pointer"
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => setDeleteTaskConfirm(null)}
                                    className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 whitespace-nowrap cursor-pointer"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteTaskConfirm(task.id)}
                                  className="w-7 h-7 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                  title="Delete task"
                                >
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-file-line text-base"></i>
                    Documents
                  </h4>
                  <button
                    onClick={() => { setShowUploadForm(!showUploadForm); setUploadSuccess(''); setUploadError(''); }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className={showUploadForm ? 'ri-close-line' : 'ri-upload-2-line'}></i>
                    {showUploadForm ? 'Cancel' : 'Upload Document'}
                  </button>
                </div>

                {/* Upload Form */}
                {showUploadForm && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <h5 className="text-xs font-semibold text-gray-900">Upload to {selectedProject.projectNumber}</h5>

                    {/* Type Toggle */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setUploadType('file')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          uploadType === 'file' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <i className="ri-file-upload-line"></i> File Upload
                      </button>
                      <button
                        onClick={() => setUploadType('text')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          uploadType === 'text' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <i className="ri-file-text-line"></i> Text Entry
                      </button>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="Document title"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                      <textarea
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        placeholder="Brief description..."
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none resize-none"
                      />
                    </div>

                    {/* File or Text */}
                    {uploadType === 'file' ? (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">File <span className="text-red-500">*</span></label>
                        <input id="file-upload-detail" type="file" onChange={(e) => { if (e.target.files?.[0]) setSelectedFile(e.target.files[0]); }} className="hidden" />
                        <label
                          htmlFor="file-upload-detail"
                          className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors cursor-pointer bg-white"
                        >
                          <div className="text-center">
                            <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-1 block"></i>
                            <p className="text-sm text-gray-600">
                              {selectedFile ? <span className="font-medium text-gray-900">{selectedFile.name}</span> : 'Click to select a file'}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">PDF, DOC, XLS, Images, etc.</p>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Content <span className="text-red-500">*</span></label>
                        <textarea
                          value={textContent}
                          onChange={(e) => setTextContent(e.target.value)}
                          placeholder="Enter text content..."
                          rows={6}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none resize-none"
                        />
                      </div>
                    )}

                    {uploadError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-600"><i className="ri-error-warning-line mr-1"></i>{uploadError}</p>
                      </div>
                    )}
                    {uploadSuccess && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-600"><i className="ri-checkbox-circle-line mr-1"></i>{uploadSuccess}</p>
                      </div>
                    )}

                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        uploading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      {uploading ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Uploading...</> : <><i className="ri-upload-2-line mr-2"></i>Upload Document</>}
                    </button>
                  </div>
                )}

                {/* Documents List */}
                {visibleItems.length === 0 ? (
                  <div className="text-center py-6">
                    <i className="ri-file-line text-3xl text-gray-200 mb-2 block"></i>
                    <p className="text-xs text-gray-500">No documents yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Click "Upload Document" to add the first file</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleItems
                      .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
                      .map((item) => (
                        <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-all">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className={`${item.type === 'text' ? 'ri-file-text-line' : getFileIcon(item.fileType)} text-lg text-gray-600`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                              {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                              <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-400">
                                {item.fileName && <span><i className="ri-file-line mr-0.5"></i>{item.fileName}</span>}
                                <span><i className="ri-calendar-line mr-0.5"></i>{new Date(item.uploadedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {item.type === 'file' && (
                                <>
                                  <button
                                    onClick={() => window.open(item.fileUrl, '_blank')}
                                    className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                    title="View"
                                  >
                                    <i className="ri-eye-line text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => { const a = document.createElement('a'); a.href = item.fileUrl!; a.download = item.fileName || 'download'; a.click(); }}
                                    className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                    title="Download"
                                  >
                                    <i className="ri-download-line text-sm"></i>
                                  </button>
                                </>
                              )}
                              {deleteConfirm === item.id ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => handleDeleteItem(item.id)} className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 whitespace-nowrap cursor-pointer">Confirm</button>
                                  <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 whitespace-nowrap cursor-pointer">Cancel</button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirm(item.id)}
                                  className="w-7 h-7 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                  title="Delete"
                                >
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
